import { useState } from 'react';
import type { EntryTimingProfile } from '@einhaus/project314-types';
import { EntryTimingProfilePanel } from '@/components/EntryTimingProfile';
import { FormSelect } from '@/components/UI';

export interface PortfolioBacktestEntryTimingProfilesProps {
    profiles: readonly EntryTimingProfile[];
}

function profileKey(profile: EntryTimingProfile): string {
    return `${profile.population}:${profile.windowRole}`;
}

function populationLabel(profile: EntryTimingProfile): string {
    return profile.population === 'signalCandidate' ? 'Signal candidates' : 'Portfolio accepted';
}

function windowLabel(profile: EntryTimingProfile): string {
    switch (profile.windowRole) {
        case 'training':
            return 'Training';
        case 'walkForward':
            return 'Walk-forward';
        case 'wholeRange':
            return 'Whole range';
    }
}

function profilePriority(profile: EntryTimingProfile): number {
    const populationPriority = profile.population === 'signalCandidate' ? 0 : 10;
    const windowPriority = profile.windowRole === 'training' ? 0 : profile.windowRole === 'walkForward' ? 1 : 2;
    return populationPriority + windowPriority;
}

export function PortfolioBacktestEntryTimingProfiles({ profiles }: PortfolioBacktestEntryTimingProfilesProps) {
    const orderedProfiles = [...profiles].sort((left, right) => profilePriority(left) - profilePriority(right));
    const defaultProfile = orderedProfiles[0];
    const [selectedProfileKey, setSelectedProfileKey] = useState(() => (defaultProfile ? profileKey(defaultProfile) : ''));
    const selectedProfile = orderedProfiles.find((profile) => profileKey(profile) === selectedProfileKey) ?? defaultProfile;

    if (!selectedProfile) return null;

    return (
        <section className="mt-3 space-y-3 p-4" aria-labelledby="portfolio-entry-timing-heading">
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                <div>
                    <h4 className="mb-1" id="portfolio-entry-timing-heading">
                        Entry Timing Profile
                    </h4>
                    <p className="max-w-4xl text-xs text-zinc-400">
                        Compare the full signal stream with trades accepted by portfolio admission. Buckets use actual opening execution
                        time in New York; use training evidence to form a timing hypothesis, then verify it against walk-forward results.
                    </p>
                </div>
                <FormSelect
                    aria-label="Entry timing population and evidence window"
                    className="min-w-64"
                    fullWidth={false}
                    noMargin
                    onChange={(event) => setSelectedProfileKey(event.target.value)}
                    options={orderedProfiles.map((profile) => ({
                        label: `${populationLabel(profile)} · ${windowLabel(profile)}`,
                        value: profileKey(profile)
                    }))}
                    size="sm"
                    value={profileKey(selectedProfile)}
                />
            </div>

            <EntryTimingProfilePanel profile={selectedProfile} title={`${populationLabel(selectedProfile)} Entry Timing`} />
        </section>
    );
}
